import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Analytics } from '@vercel/analytics/react'
import { 
  Mic, 
  Code, 
  Brain, 
  Zap, 
  Users, 
  Star, 
  ArrowRight, 
  Play,
  CheckCircle,
  MessageCircle,
  Lightbulb,
  Target,
  Headphones,
  Volume2,
  X
} from 'lucide-react'

const LandingPage: React.FC = () => {
  const navigate = useNavigate()
  const [showDemo, setShowDemo] = useState(false)

  const features = [
    {
      icon: Volume2,
      title: 'AI Voice Mentor',
      description: 'Get real-time voice feedback from an AI senior engineer as you code'
    },
    {
      icon: Brain,
      title: 'Intelligent Guidance',
      description: 'AI analyzes your code and provides personalized suggestions and explanations'
    },
    {
      icon: Target,
      title: 'Problem Solving',
      description: 'Practice with curated coding problems across multiple languages'
    },
    {
      icon: Zap,
      title: 'Instant Feedback',
      description: 'Receive immediate voice guidance on code quality, best practices, and optimizations'
    },
    {
      icon: Headphones,
      title: 'Voice Learning',
      description: 'Learn through natural voice conversations with your AI coding mentor'
    },
    {
      icon: CheckCircle,
      title: 'Test Your Solutions',
      description: 'Run test cases to validate your code and track progress'
    }
  ]

  const testimonials = [
    {
      name: 'Sarah Chen',
      content: 'DevWhisper is like having a senior engineer pair programming with me. The voice feedback catches issues I would have missed.',
      rating: 5
    },
    {
      name: 'Marcus Rodriguez',
      content: 'The AI mentor explains complex concepts in simple terms. It\'s transformed how I approach problem solving.',
      rating: 5
    },
    {
      name: 'Emily Watson',
      content: 'Perfect for learning new concepts. The voice guidance makes coding feel more natural and engaging.',
      rating: 5
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50">
      {/* Navigation */}
      <nav className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-600 rounded-lg">
              <Code className="h-6 w-6 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">DevWhisper</span>
          </div>
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => navigate('/login')}
              className="text-gray-600 hover:text-gray-900 transition-colors"
            >
              Sign In
            </button>
            <button 
              onClick={() => navigate('/login')}
              className="btn-primary"
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20 text-center">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Code with an AI
            <span className="text-primary-600"> Senior Engineer</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Get real-time voice feedback from an AI mentor as you code. 
            Learn best practices, catch bugs early, and improve your skills with personalized guidance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button 
              onClick={() => navigate('/login')}
              className="btn-primary text-lg px-8 py-4 flex items-center space-x-2"
            >
              <Play className="h-5 w-5" />
              <span>Start Coding Now</span>
            </button>
            <button 
              onClick={() => setShowDemo(true)}
              className="btn-secondary text-lg px-8 py-4 flex items-center space-x-2"
            >
              <span>Watch Demo</span>
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Why Developers Love DevWhisper
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Experience the future of coding with AI-powered voice mentorship and real-time guidance.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div key={index} className="card text-center hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <feature.icon className="h-6 w-6 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
              <p className="text-gray-600">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className="container mx-auto px-4 py-20 bg-white">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            How It Works
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Get started in three simple steps
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          <div className="text-center">
            <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
              1
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Choose a Problem</h3>
            <p className="text-gray-600">Select from our curated collection of coding challenges</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
              2
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Code Normally</h3>
            <p className="text-gray-600">Write code in your preferred language with our powerful editor</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
              3
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Get Voice Feedback</h3>
            <p className="text-gray-600">Receive real-time voice guidance from your AI mentor</p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="container mx-auto px-4 py-20">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Loved by Developers Worldwide
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="card">
              <div className="flex items-center mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-4 italic">"{testimonial.content}"</p>
              <div>
                <p className="font-semibold text-gray-900">{testimonial.name}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="card text-center max-w-3xl mx-auto bg-gradient-to-r from-primary-600 to-primary-700 text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Transform Your Coding?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands of developers who are already coding smarter with DevWhisper.
          </p>
          <button 
            onClick={() => navigate('/login')}
            className="bg-white text-primary-600 font-semibold py-3 px-8 rounded-lg hover:bg-gray-100 transition-colors text-lg"
          >
            Start Your Free Trial
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-primary-600 rounded-lg">
                  <Code className="h-6 w-6 text-white" />
                </div>
                <span className="text-xl font-bold">DevWhisper</span>
              </div>
              <p className="text-gray-400">
                The future of coding with AI-powered voice interaction.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Product</h3>
              <ul className="space-y-2 text-gray-400">
                <li>Features</li>
                <li>Pricing</li>
                <li>API</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Company</h3>
              <ul className="space-y-2 text-gray-400">
                <li>About</li>
                <li>Blog</li>
                <li>Careers</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-gray-400">
                <li>Help Center</li>
                <li>Contact</li>
                <li>Status</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 DevWhisper. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Demo Video Modal */}
      {showDemo && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-xl font-semibold text-gray-900">DevWhisper Demo</h3>
              <button
                onClick={() => setShowDemo(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-4">
              <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                <video
                  className="w-full h-full object-cover"
                  controls
                  autoPlay
                  muted
                  loop
                >
                  <source src="/demo-wshiper.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-600 mb-4">
                  Watch how DevWhisper provides real-time AI voice feedback while you code
                </p>
                <button
                  onClick={() => navigate('/login')}
                  className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Try It Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Analytics />
    </div>
  )
}

export default LandingPage 